import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ArrowLeft, Video, FileText, ExternalLink, Lightbulb } from 'lucide-react';
import { StepCard, VideoGrid, InfoCard } from './SectionComponents';

const SetupForSuccessSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext, streamType = 'generalist', exampleVideos = [] }) => {
  const profileTips = [
    {
      title: 'Keep it relevant',
      description: 'Focus on achievements from the last 1–2 years that relate to your Jumpstart stream.'
    },
    {
      title: 'Avoid first person',
      description: 'Write "Worked at…" instead of "I worked at…" to match professional style.'
    },
    {
      title: 'Show impact',
      description: 'Use numbers where possible, e.g. "Scaled partnerships to 15+ clients, driving £250k ARR."'
    }
  ];

  const cvPractices = [
    'Keep it to one page: concise and easy to scan.',
    'Structure: Education → Work Experience → Extracurricular → Additional Experience.',
    'Focus on outcomes, not tasks: "Increased user retention by 20%" > "Worked on user retention."',
    'Use clean formatting: avoid colours/graphics/complex designs. Follow a simple, professional template if unsure.',
    'Use a PDF to preserve formatting.'
  ];

  const videoDo = [
    'Compelling hook — start with something memorable.',
    'Biggest achievements — highlight 1–2 impressive wins with impact.',
    'Motivation — why startups, and why now.',
    'Personality — let natural energy come through.'
  ];

  const videoDont = [
    'Start with a generic "Hi I am X and I graduated from Y".',
    'Read off a script.',
    'Chronicle your CV.',
    'Use buzzwords (e.g., "hardworking") without concrete examples.'
  ];

  const uploadSteps = [
    'Draft a quick outline (don’t script word-for-word).',
    'Go to Loom and sign up.',
    'Click “New video” → “Record video”.',
    'Set mode to “Camera only”.',
    'Check lighting, sound, and framing (smart casual is fine).',
    'Record your intro (≤ 1m30s, landscape).',
    'Preview & re-record if needed.',
    'Copy your Loom link → add it to your Jumpstart profile.'
  ];

  const streamLabel = streamType === 'sales' ? 'sales' : (streamType === 'marketing' ? 'marketing' : 'generalist');

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">Setting yourself up for success</h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Many startups receive dozens of applications per role. A polished profile, strong CV and engaging video intro helps you rise to the top.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          <StepCard title="1) Check your profile 📝">
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                Your profile is the first thing startups see when they open your application. The Jumpstart team reviews every profile — if you haven’t heard from us, you’re already in a good place. You can tweak or update it anytime, especially after new achievements.
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                {profileTips.map((tip, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-2">{tip.title}</h4>
                    <p className="text-gray-700 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="2) Level up your CV 📄">
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                Update your CV anytime by heading to the Profile tab and pasting a new link. Keep it polished and up-to-date.
              </p>
              <div className="space-y-3">
                {cvPractices.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1" />
                    <p className="text-gray-800 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="3) Make your video intro stand out 🎥">
            <div className="space-y-10">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-white rounded-2xl p-6 border border-emerald-200/60">
                  <h4 className="font-semibold text-gray-900 mb-4">Do:</h4>
                  <div className="space-y-3">
                    {videoDo.map((i, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-600 mt-1" />
                        <p className="text-gray-800 leading-relaxed">{i}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-rose-200/60">
                  <h4 className="font-semibold text-gray-900 mb-4">Do not:</h4>
                  <div className="space-y-3">
                    {videoDont.map((i, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <XCircle className="w-5 h-5 text-rose-500 mt-1" />
                        <p className="text-gray-800 leading-relaxed">{i}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-bold text-gray-900 text-lg">Stellar past examples:</h4>
                {exampleVideos.length > 0 ? (
                  <VideoGrid videos={exampleVideos} columns={3} />
                ) : (
                  <InfoCard
                    title="Examples coming soon"
                    description="We’ll soon embed curated videos tailored to your stream here."
                    type="info"
                  />
                )}
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">How to upload/update the video</h4>
                <div className="grid gap-2 md:grid-cols-2">
                  {uploadSteps.map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-[#02B491] rounded-full text-white font-bold text-xs flex items-center justify-center mt-0.5">{i + 1}</div>
                      <p className="text-gray-800 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetupForSuccessSection;


